import React, { memo, useEffect, useLayoutEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router';
import ReactGA from 'react-ga4';

import { Header, NavigationSidebar } from 'components/layouts';
import useLayout from 'components/layouts/hooks/useLayout';
import WarningWrapper from 'components/layouts/WarningWrapper';
import BackToTop from 'components/BackToTop';
import { hasRestoreIntent } from 'components/layouts/transactionsRestoreIntent';

function Layout() {
  const location = useLocation();

  const { closeSidebarOnMobileLinkClick, toggleSidebar, isNavigationSidebarOpen } = useLayout();

  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    ReactGA.send({ hitType: 'pageview', page: window.location.pathname + window.location.search });
  }, [location]);

  useLayoutEffect(() => {
    if (hasRestoreIntent()) return;
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [location.pathname]);

  useEffect(() => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setVH();
    window.addEventListener('resize', setVH);
    window.addEventListener('orientationchange', setVH);

    return () => {
      window.removeEventListener('resize', setVH);
      window.removeEventListener('orientationchange', setVH);
    };
  }, []);

  return (
    <>
      <style>{`
        .mobile-vh {
          height: 100vh;
          height: calc(var(--vh, 1vh) * 100);
        }
        
        @supports (height: 100dvh) {
          .mobile-vh {
            height: 100dvh;
          }
        }
        
        .safe-padding-bottom {
          padding-bottom: env(safe-area-inset-bottom, 3.5rem);
        }
      `}</style>

      <WarningWrapper />
      <div className="mobile-vh flex min-h-0 w-screen bg-primaryBeige">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <Header toggleMobileMenu={toggleSidebar} />
          <div className="flex min-h-0 min-w-0 flex-1">
            <NavigationSidebar
              setSidebarOpen={toggleSidebar}
              isOpen={isNavigationSidebarOpen ?? false}
              onMenuLinkClick={closeSidebarOnMobileLinkClick}
            />

            <main ref={contentRef} className="min-h-0 min-w-0 flex-1 overflow-y-auto p-10 px-4 xl:px-20">
              <Outlet />
            </main>
            <BackToTop scrollContainerRef={contentRef} />
          </div>
        </div>
      </div>
    </>
  );
}

export default memo(Layout);
